import { API_BASE } from './apiBase';
// src/SideNav.js
import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ExpenseLedger from './finances/ExpenseLedger';
import NotificationBell from './NotificationBell';

function SideNav() {
  const location = useLocation();
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });
  const navRef = useRef(null);
  // Each section opens/closes INDEPENDENTLY (not an accordion).
  const [openSections, setOpenSections] = useState({}); // { billing:true, finances:true, ... }
  const isOpen = (s) => !!openSections[s];

  const role = localStorage.getItem('role') || 'employee';
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const isSuperAdmin = role === 'superadmin';
  const isPM = role === 'projectManager';

  // PM permission gating. Super Admin / Admin are unaffected. Until /me loads,
  // default to visible (undefined !== false), so nothing flashes hidden.
  const navigate = useNavigate();
  const isEmployee = role === 'employee';
  const [perms, setPerms] = useState(null);
  const [empInv, setEmpInv] = useState(() => localStorage.getItem('hasInventoryAccess') === 'true');
  useEffect(() => {
    if (!isPM && role !== 'admin' && !isEmployee) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_BASE}/me`, { headers: { Authorization: token } })
      .then(res => {
        setPerms(res.data?.permissions || {});
        if (isEmployee) setEmpInv(res.data?.hasInventoryAccess === true);
      })
      .catch(() => {});
  }, [isPM, role, isEmployee]);
  // Portal/Website toggle: super admin always; plain admin only with the
  // websiteEditor permission granted under Settings › Users.
  const websiteAllowed = isSuperAdmin || (role === 'admin' && perms?.websiteEditor === true);
  const isWebsite = location.pathname.startsWith('/website');
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  useEffect(() => {
    if (!modeMenuOpen) return;
    const close = () => setModeMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [modeMenuOpen]);
  // Visibility per role. Employees: Projects yes, Inventory only when granted,
  // Billing / Employees / Timecards NEVER. PMs keep their permission toggles.
  const can = (key) => {
    if (isEmployee) {
      if (key === 'projects') return true;
      if (key === 'inventory') return empInv;
      return false; // billing, employees, timecards — never
    }
    return !isPM || !perms || perms[key] !== false;
  };
  const schedulingVisible = !isEmployee && (!isPM || !perms || perms.scheduling !== 'hidden');

  // Slide the indicator to whichever VISIBLE nav item best matches the current
  // route (longest data-nav prefix wins). When a section collapses, its hidden
  // sub-items drop out of the running and the bar lands on the section header.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const path = location.pathname;
    let best = null, bestLen = -1;
    nav.querySelectorAll('[data-nav], a[href]').forEach(el => {
      const route = el.getAttribute('data-nav') || el.getAttribute('href');
      if (!route) return;
      const matches = route === '/'
        ? path === '/'
        : (path === route || path.startsWith(route + '/'));
      if (matches && route.length > bestLen) { best = el; bestLen = route.length; }
    });
    if (best) {
      const r = best.getBoundingClientRect();
      const nr = nav.getBoundingClientRect();
      setIndicatorStyle({ top: r.top - nr.top + nav.scrollTop + 4, height: r.height - 8 });
    } else {
      setIndicatorStyle({ top: 0, height: 0 });
    }
  }, [location.pathname, openSections]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Auto-expand a section when you land inside it from elsewhere (e.g. clicking
  // a quote row on the Billing dashboard → /billing/create-quote should show
  // the expanded Billing menu with Create Quote highlighted).
  useEffect(() => {
    const p = location.pathname;
    const sectionRoots = { billing: '/billing', employees: '/employees', finances: '/finances' };
    Object.entries(sectionRoots).forEach(([sec, root]) => {
      if (p.startsWith(root + '/') && !openSections[sec]) {
        setOpenSections(prev => ({ ...prev, [sec]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

const handleLogout = () => {
  // 1. Rescue the saved email before the wipe
  const savedEmail = localStorage.getItem('savedEmail'); 
  
  // 2. Nuke all the sensitive session data
  localStorage.clear(); 
  
  // 3. Put the email safely back in its spot
  if (savedEmail) {
    localStorage.setItem('savedEmail', savedEmail);
  }
  
  // 4. Send them to the login screen
  window.location.href = '/login'; 
};

// Smart Navigation Logic for Projects
  const projectLinkDest = location.pathname.startsWith('/projects') 
    ? '/projects' 
    : (localStorage.getItem('lastProjectUrl') || '/projects');

  return (
    // max-md:h-[100dvh]: iOS's 100vh hides the bottom (bell/logout) behind
    // Safari's toolbar — dvh tracks the real visible height. Desktop unchanged.
    <div className="w-72 bg-zinc-950 text-zinc-300 h-screen max-md:h-[100dvh] fixed left-0 top-0 z-50 flex flex-col border-r border-zinc-900/10">
      {/* Logo / Title */}
      <div className="px-6 pt-4 pb-5 border-b border-zinc-900/10">
        <NavLink
          to="/"
          className="flex items-center hover:opacity-90 transition-opacity"
        >
          <span className="text-lg font-bold text-white">Stalwart Electric</span>
        </NavLink>

        {/* Portal / Website picker — discreet all-caps dropdown, no arrow.
            Visible to super admin or an admin with the websiteEditor permission. */}
        {websiteAllowed && (
          <div className="relative mt-1">
            <button
              onClick={(e) => { e.stopPropagation(); setModeMenuOpen((o) => !o); }}
              className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {isWebsite ? 'Website' : 'Portal'}
            </button>
            {modeMenuOpen && (
              <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[110px] rounded-md bg-zinc-900 border border-zinc-800 shadow-xl shadow-black/40 py-1">
                {[{ label: 'Portal', to: '/', active: !isWebsite }, { label: 'Website', to: '/website', active: isWebsite }]
                  .filter((o) => !o.active)
                  .map((o) => (
                    <button
                      key={o.to}
                      onClick={() => { setModeMenuOpen(false); navigate(o.to); }}
                      className="block w-full text-left px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors"
                    >
                      {o.label}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable Nav */}
      <nav ref={navRef} className="flex-1 px-3 py-6 overflow-y-auto relative">
        {/* Sliding indicator */}
        <div
          className="absolute left-0 w-1 bg-indigo-500 transition-all duration-300 ease-out pointer-events-none"
          style={{
            top: indicatorStyle.top,
            height: indicatorStyle.height,
          }}
        />

        <ul className="space-y-1">
          {/* Customers — hidden from Project Managers and Employees */}
          {!isPM && !isEmployee && (
          <li>
            <NavLink
              to="/customers"
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                  isActive
                    ? 'text-indigo-400 bg-zinc-800/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                }`
              }
            >
              Customers
            </NavLink>
          </li>
          )}

          {/* Contacts (People / Relationships / Bids & Jobs) — admins and up */}
          {['admin', 'superadmin'].includes(localStorage.getItem('role')) && (
          <li>
            <NavLink
              to="/contacts"
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                  isActive
                    ? 'text-indigo-400 bg-zinc-800/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                }`
              }
            >
              Contacts
            </NavLink>
          </li>
          )}

          {/* Projects */}
          {can('projects') && (
          <li>
            <NavLink
              to={projectLinkDest}
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                  isActive || location.pathname.startsWith('/projects')
                    ? 'text-indigo-400 bg-zinc-800/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                }`
              }
            >
              Projects
            </NavLink>
          </li>
          )}

          {/* Employees */}
          {can('employees') && (
          <li>
            <NavLink
              to="/employees"
              data-nav="/employees"
              onClick={() => toggleSection('employees')}
              className={({ isActive }) =>
                `w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                  isActive || location.pathname.startsWith('/employees')
                    ? 'text-indigo-400 bg-zinc-800/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                }`
              }
            >
              <span>Employees</span>
              <span
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSection('employees'); }}
                title="Expand / collapse"
                className="-my-2 -mr-3 ml-1 flex items-center justify-center w-9 h-9 max-md:w-11 max-md:h-11 rounded-lg hover:bg-zinc-800/60 active:bg-zinc-800 text-zinc-500 hover:text-zinc-200 shrink-0"
              >
                <span className={`text-sm max-md:text-lg leading-none transition-transform ${isOpen('employees') ? 'rotate-90' : ''}`}>›</span>
              </span>
            </NavLink>

            {/* Sub-links under Employees */}
            {isOpen('employees') && (
            <ul className="mt-1 pl-8 space-y-1">
              {schedulingVisible && (
              <li>
                <NavLink
                  to="/employees/scheduling"
                  data-nav="/employees/scheduling"
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm rounded transition-colors ${
                      isActive
                        ? 'text-indigo-400 bg-zinc-800/50'
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                    }`
                  }
                >
                  Scheduling{isPM ? ' (view only)' : ''}
                </NavLink>
              </li>
              )}
              {can('timecards') && (
              <li>
                <NavLink
                  to="/employees/timecards"
                  data-nav="/employees/timecards"
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm rounded transition-colors ${
                      isActive
                        ? 'text-indigo-400 bg-zinc-800/50'
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                    }`
                  }
                >
                  Timecards
                </NavLink>
              </li>
              )}
            </ul>
            )}
          </li>
          )}

          {/* Inventory */}
          {can('inventory') && (
          <li>
            <NavLink
              to="/inventory"
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                  isActive
                    ? 'text-indigo-400 bg-zinc-800/50'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                }`
              }
            >
              Inventory
            </NavLink>
          </li>
          )}

          {/* Billing */}
          {can('billing') && (
          <li>
  <NavLink
    to="/billing"
    onClick={() => toggleSection('billing')}
    data-section="billing"
    className={({ isActive }) =>
      `w-full flex items-center justify-between text-left px-4 py-2.5 text-sm font-medium rounded transition-colors ${
        isActive || isOpen('billing') || location.pathname.startsWith('/billing')
          ? 'text-indigo-400 bg-zinc-800/50'
          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
      }`
    }
  >
    <span>Billing</span>
    <span
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSection('billing'); }}
                title="Expand / collapse"
                className="-my-2 -mr-3 ml-1 flex items-center justify-center w-9 h-9 max-md:w-11 max-md:h-11 rounded-lg hover:bg-zinc-800/60 active:bg-zinc-800 text-zinc-500 hover:text-zinc-200 shrink-0"
              >
                <span className={`text-sm max-md:text-lg leading-none transition-transform ${isOpen('billing') ? 'rotate-90' : ''}`}>›</span>
              </span>
  </NavLink>

  {isOpen('billing') && (
    <ul className="mt-1 pl-8 space-y-1">
      <li>
        <NavLink
          to="/billing/create-quote"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Quote
        </NavLink>
      </li>
      <li>
        <NavLink
          to="/billing/create-proposal"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Proposal
        </NavLink>
      </li>
      <li>
        <NavLink
          to="/billing/create-invoice"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Invoice
        </NavLink>
      </li>
      <li>
        <NavLink
          to="/billing/create-change-order"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Change Order
        </NavLink>
      </li>
      <li>
        <NavLink
          to="/billing/create-receipt"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Receipt
        </NavLink>
      </li>
      {!isPM && (
      <li>
        <NavLink
          to="/billing/create-contract"
          className={({ isActive }) =>
            `block px-4 py-2 text-sm rounded transition-colors ${
              isActive
                ? 'text-indigo-400 bg-zinc-800/50'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
            }`
          }
        >
          Contract
        </NavLink>
      </li>
      )}
    </ul>
  )}
</li>
          )}

          {/* Finances - superadmin only */}
          {isSuperAdmin && (
            <li>
              <NavLink
                to="/finances"
                onClick={() => toggleSection('finances')}
                data-section="finances"
                data-nav="/finances"
                className={({ isActive }) =>
                  `w-full flex items-center justify-between text-left px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                    isActive || isOpen('finances') || location.pathname.startsWith('/finances')
                      ? 'text-indigo-400 bg-zinc-800/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
                  }`
                }
              >
                <span>Finances</span>
                <span
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSection('finances'); }}
                title="Expand / collapse"
                className="-my-2 -mr-3 ml-1 flex items-center justify-center w-9 h-9 max-md:w-11 max-md:h-11 rounded-lg hover:bg-zinc-800/60 active:bg-zinc-800 text-zinc-500 hover:text-zinc-200 shrink-0"
              >
                <span className={`text-sm max-md:text-lg leading-none transition-transform ${isOpen('finances') ? 'rotate-90' : ''}`}>›</span>
              </span>
              </NavLink>

              {isOpen('finances') && (
                <ul className="mt-1 pl-8 space-y-1">
                  <li>
                    <NavLink
                      to="/finances/projects"
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm rounded transition-colors ${
                          isActive
                            ? 'text-indigo-400 bg-zinc-800/50'
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                        }`
                      }
                    >
                      Projects
                    </NavLink>
                  </li>
                  <li>
  <NavLink
    to="/finances/ExpenseLedger"
    className={({ isActive }) =>
      `block px-4 py-2 text-sm rounded transition-colors ${
        isActive
          ? 'text-indigo-400 bg-zinc-800/50'
          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
      }`
    }
  >
    Expense Ledger
  </NavLink>
</li>
                  <li>
  <NavLink
    to="/finances/price-book"
    className={({ isActive }) =>
      `block px-4 py-2 text-sm rounded transition-colors ${
        isActive
          ? 'text-indigo-400 bg-zinc-800/50'
          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
      }`
    }
  >
    Price Book
  </NavLink>
</li>
                  <li>
  <NavLink
    to="/finances/audit"
    className={({ isActive }) =>
      `block px-4 py-2 text-sm rounded transition-colors ${
        isActive
          ? 'text-indigo-400 bg-zinc-800/50'
          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
      }`
    }
  >
    Audit Trail
  </NavLink>
</li>
                </ul>
              )}
            </li>
          )}

          {/* Settings */}
          <li>
            <button
              onClick={() => toggleSection('settings')}
              data-section="settings"
              data-nav="/settings"
              className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm font-medium rounded transition-colors ${
                isOpen('settings') || location.pathname.startsWith('/settings')
                  ? 'text-indigo-400 bg-zinc-800/50'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/30'
              }`}
            >
              <span>Settings</span>
              <span className={`text-sm max-md:text-lg leading-none text-zinc-500 transition-transform ${isOpen('settings') ? 'rotate-90' : ''}`}>›</span>
            </button>

            {isOpen('settings') && (
              <ul className="mt-1 pl-8 space-y-1">
                <li>
                  <NavLink
                    to="/settings/profile"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm rounded transition-colors ${
                        isActive
                          ? 'text-indigo-400 bg-zinc-800/50'
                          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                      }`
                    }
                  >
                    Profile
                  </NavLink>
                </li>
                {!isEmployee && (
                <li>
                  <NavLink
                    to="/settings/company"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm rounded transition-colors ${
                        isActive
                          ? 'text-indigo-400 bg-zinc-800/50'
                          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                      }`
                    }
                  >
                    Company Info
                  </NavLink>
                </li>
                )}
                {isAdmin && (
                  <li>
                    <NavLink
                      to="/settings/users"
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm rounded transition-colors ${
                          isActive
                            ? 'text-indigo-400 bg-zinc-800/50'
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                        }`
                      }
                    >
                      Users & Permissions
                    </NavLink>
                  </li>
                )}
                {localStorage.getItem('role') === 'superadmin' && (
                  <li>
                    <NavLink
                      to="/settings/portal"
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm rounded transition-colors ${
                          isActive
                            ? 'text-indigo-400 bg-zinc-800/50'
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                        }`
                      }
                    >
                      Portal Settings
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>
        </ul>
      </nav>

      {/* Notifications + Logout */}
      <div className="border-t border-zinc-800/50 mt-auto pt-2">
        {/* Notification bell sits directly above logout */}
        <NotificationBell />
      </div>
      <div className="px-3 pb-4">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
            />
          </svg>
          Logout
        </button>
      </div>
    </div>
  );
}

export default SideNav;